import React, { useEffect, useState, useCallback } from "react";
import { Lote, Item, SimularLanceResponse, EstatisticaAgioTipo } from "../types";
import { DollarSign, Percent, CheckCircle2, TrendingUp, Sparkles, Truck, ShieldAlert, RotateCcw } from "lucide-react";
import { fetchWithAuth } from "../services/api";

interface SimuladorLanceProps {
  lote: Lote;
  itens: Item[];
}

export const SimuladorLance: React.FC<SimuladorLanceProps> = ({ lote }) => {
  // Entradas do operador
  const valorMinimo = lote.valor_minimo || 0;
  const valorAvaliacao = lote.valor_avaliacao || 0;
  const totalUnidades = Math.max(1, lote.qtde_total_unidades || 1);

  const [lance, setLance] = useState<number>(valorMinimo);
  const [aliquotaIcms, setAliquotaIcms] = useState<number>(18.0);
  const [taxaComissao, setTaxaComissao] = useState<number>(5.0);
  const [custoFrete, setCustoFrete] = useState<number>(0.0);
  const [outrasDespesas, setOutrasDespesas] = useState<number>(0.0);

  // Dados calculados via Worker ou localmente para resposta instantânea
  const [resultado, setResultado] = useState<SimularLanceResponse | null>(null);
  const [estatisticaTipo, setEstatisticaTipo] = useState<EstatisticaAgioTipo | null>(null);

  // 1. Carregar estatísticas do edital para o tipo deste lote
  useEffect(() => {
    async function carregarEstatisticas() {
      try {
        const res = await fetchWithAuth(`/api/historico/estatisticas/${lote.edital_id}`);
        if (res.ok) {
          const data = await res.json();
          if (data.estatisticasPorTipo && lote.tipo) {
            const match = data.estatisticasPorTipo.find(
              (t: EstatisticaAgioTipo) => t.tipo?.toUpperCase() === lote.tipo?.toUpperCase()
            );
            setEstatisticaTipo(match || null);
          }
        }
      } catch (err) {
        console.warn("Erro ao buscar histórico de ágio:", err);
      }
    }
    carregarEstatisticas();
  }, [lote.edital_id, lote.tipo]);

  // 2. Chamar simulação no Worker
  const simularNoWorker = useCallback(async (lanceValor: number) => {
    try {
      const res = await fetchWithAuth("/api/markup/simular-lance", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lote_id: lote.id,
          lance: lanceValor,
          valor_minimo: valorMinimo,
          valor_avaliacao: valorAvaliacao,
          qtde_total_unidades: totalUnidades,
          aliquota_icms: aliquotaIcms,
          taxa_comissao: taxaComissao,
          custo_frete: custoFrete,
          outras_despesas: outrasDespesas,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setResultado(data);
      }
    } catch (err) {
      console.error("Erro ao simular lance:", err);
    }
  }, [lote.id, valorMinimo, valorAvaliacao, totalUnidades, aliquotaIcms, taxaComissao, custoFrete, outrasDespesas]);

  // Recalcular sempre que qualquer premissa mudar
  useEffect(() => {
    simularNoWorker(lance);
  }, [lance, simularNoWorker]);

  // Atalhos rápidos de lance
  const aplicarLanceMinimo = () => setLance(valorMinimo);
  const aplicarAcrescimo = (pct: number) => setLance(Number((valorMinimo * (1 + pct / 100)).toFixed(2)));
  const aplicarLanceEstimado = () => {
    if (resultado?.previsao_lance_estimado?.lance_estimado_agio) {
      setLance(resultado.previsao_lance_estimado.lance_estimado_agio);
    } else if (valorAvaliacao > 0) {
      setLance(Number((valorAvaliacao * 0.708).toFixed(2)));
    } else {
      setLance(Number((valorMinimo * 1.636).toFixed(2)));
    }
  };
  const aplicarTetoOportunidade = () => {
    if (valorAvaliacao > 0) {
      // Lance cujo desembolso atinja 60% da avaliação
      const tetoDesembolso = valorAvaliacao * 0.60;
      const divisor = 1 + (aliquotaIcms + taxaComissao) / 100;
      const lanceAprox = Math.max(valorMinimo, (tetoDesembolso - custoFrete - outrasDespesas) / divisor);
      setLance(Number(lanceAprox.toFixed(2)));
    }
  };

  // Slider bounds
  const sliderMax = valorAvaliacao > 0
    ? Math.max(valorAvaliacao, valorMinimo * 2.5)
    : valorMinimo * 3.0;

  const custoUnitarioLancePuro = totalUnidades > 0 ? (Number(lance) || 0) / totalUnidades : (Number(lance) || 0);
  const custoUnitarioMedio = resultado ? (Number(resultado.analise_unitaria?.custo_unitario_medio) || 0) : custoUnitarioLancePuro;
  const avaliacaoUnitaria = (Number(valorAvaliacao) > 0 && totalUnidades > 0) ? Number(valorAvaliacao) / totalUnidades : null;
  const desembolsoTotal = resultado ? (Number(resultado.custos_adicionais?.desembolso_total) || 0) : (Number(lance) || 0);
  const percentualAvaliacao = resultado?.benchmark_avaliacao?.percentual_da_avaliacao;
  const descontoAvaliacao = resultado?.benchmark_avaliacao?.desconto_sobre_avaliacao;
  const classificacao = resultado?.benchmark_avaliacao?.classificacao || "COMPETITIVO_MODERADO";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {/* 1. CARDS DE REFERÊNCIA OFICIAL DA RECEITA FEDERAL */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: "12px",
      }}>
        <div style={{
          background: "rgba(255, 255, 255, 0.03)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          borderRadius: "10px",
          padding: "14px",
        }}>
          <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", textTransform: "uppercase" }}>
            Lance Mínimo (RFB)
          </span>
          <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "#f8fafc", marginTop: "4px" }}>
            R$ {(Number(valorMinimo) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
          </div>
          <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
            R$ {((Number(valorMinimo) || 0) / Math.max(1, totalUnidades)).toLocaleString("pt-BR", { minimumFractionDigits: 2 })} / un
          </span>
        </div>

        <div style={{
          background: valorAvaliacao > 0 ? "rgba(59, 130, 246, 0.08)" : "rgba(255, 255, 255, 0.03)",
          border: valorAvaliacao > 0 ? "1px solid rgba(59, 130, 246, 0.3)" : "1px solid rgba(255, 255, 255, 0.08)",
          borderRadius: "10px",
          padding: "14px",
        }}>
          <span style={{ fontSize: "0.75rem", color: valorAvaliacao > 0 ? "#60a5fa" : "var(--text-muted)", textTransform: "uppercase", fontWeight: 600 }}>
            Avaliação Oficial (RFB)
          </span>
          <div style={{ fontSize: "1.25rem", fontWeight: 700, color: valorAvaliacao > 0 ? "#93c5fd" : "#94a3b8", marginTop: "4px" }}>
            {valorAvaliacao > 0
              ? `R$ ${(Number(valorAvaliacao) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
              : "Não declarada"}
          </div>
          <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
            {avaliacaoUnitaria
              ? `R$ ${(Number(avaliacaoUnitaria) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })} / un`
              : "Base do lote"}
          </span>
        </div>

        <div style={{
          background: "rgba(255, 255, 255, 0.03)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          borderRadius: "10px",
          padding: "14px",
        }}>
          <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", textTransform: "uppercase" }}>
            Volume Físico do Lote
          </span>
          <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "#f8fafc", marginTop: "4px" }}>
            {(Number(totalUnidades) || 0).toLocaleString("pt-BR")} unidades
          </div>
          <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
            {lote.qtde_itens_distintos || 0} {(lote.qtde_itens_distintos === 1) ? "item" : "itens distintos"}
          </span>
        </div>

        <div style={{
          background: "rgba(234, 179, 8, 0.08)",
          border: "1px solid rgba(234, 179, 8, 0.25)",
          borderRadius: "10px",
          padding: "14px",
        }}>
          <span style={{ fontSize: "0.75rem", color: "#facc15", textTransform: "uppercase", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px" }}>
            <Sparkles size={13} /> Previsão Lance Estimado
          </span>
          <div style={{ fontSize: "1.25rem", fontWeight: 700, color: "#fef08a", marginTop: "4px" }}>
            {resultado?.previsao_lance_estimado?.lance_estimado_agio
              ? `R$ ${(Number(resultado.previsao_lance_estimado.lance_estimado_agio) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
              : `R$ ${(Number(valorMinimo * 1.636) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`}
          </div>
          <span style={{ fontSize: "0.72rem", color: "#fef08a", opacity: 0.85 }}>
            {estatisticaTipo ? `Ágio mediano de +${(Number(estatisticaTipo.agio_mediana) || 0).toFixed(1)}%` : "Base: histórico RFB"}
          </span>
        </div>
      </div>

      {/* 2. ÁREA DE CONTROLE DO LANCE (INPUT + SLIDER + ATALHOS) */}
      <div style={{
        background: "var(--bg-secondary)",
        border: "1px solid var(--border-color)",
        borderRadius: "12px",
        padding: "18px",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", flexWrap: "wrap", gap: "10px" }}>
          <div>
            <label style={{ fontSize: "0.95rem", fontWeight: 700, color: "#f8fafc", display: "flex", alignItems: "center", gap: "8px" }}>
              <DollarSign size={18} color="#3b82f6" />
              Seu Lance Pretendido
            </label>
            <span style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
              Ajuste o valor para simular custos acumulados e o custo unitário resultante em tempo real.
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ color: "#3b82f6", fontWeight: 700, fontSize: "1.1rem" }}>R$</span>
            <input
              type="number"
              min={valorMinimo}
              step={100}
              value={lance}
              onChange={(e) => setLance(Math.max(0, Number(e.target.value)))}
              style={{
                background: "#0b0f19",
                border: "1px solid #3b82f6",
                borderRadius: "8px",
                padding: "8px 12px",
                color: "#ffffff",
                fontSize: "1.2rem",
                fontWeight: 700,
                width: "180px",
                textAlign: "right",
              }}
            />
          </div>
        </div>

        {/* Slider Contínuo */}
        <input
          type="range"
          min={valorMinimo}
          max={sliderMax}
          step={50}
          value={lance}
          onChange={(e) => setLance(Number(e.target.value))}
          style={{
            width: "100%",
            height: "8px",
            borderRadius: "4px",
            background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${Math.min(100, Math.max(0, ((lance - valorMinimo) / (sliderMax - valorMinimo)) * 100))}%, #1e293b ${Math.min(100, Math.max(0, ((lance - valorMinimo) / (sliderMax - valorMinimo)) * 100))}%, #1e293b 100%)`,
            cursor: "pointer",
            marginBottom: "14px",
          }}
        />

        {/* Botões Rápidos */}
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
          <button
            onClick={aplicarLanceMinimo}
            className="btn btn-outline"
            style={{ fontSize: "0.75rem", padding: "5px 10px" }}
          >
            <RotateCcw size={13} /> Mínimo (R$ {(Number(valorMinimo) || 0).toLocaleString("pt-BR")})
          </button>
          <button
            onClick={() => aplicarAcrescimo(10)}
            className="btn btn-outline"
            style={{ fontSize: "0.75rem", padding: "5px 10px" }}
          >
            +10%
          </button>
          <button
            onClick={() => aplicarAcrescimo(25)}
            className="btn btn-outline"
            style={{ fontSize: "0.75rem", padding: "5px 10px" }}
          >
            +25%
          </button>
          <button
            onClick={() => aplicarAcrescimo(50)}
            className="btn btn-outline"
            style={{ fontSize: "0.75rem", padding: "5px 10px" }}
          >
            +50%
          </button>
          <button
            onClick={aplicarLanceEstimado}
            className="btn btn-outline"
            style={{
              fontSize: "0.75rem",
              padding: "5px 10px",
              borderColor: "#eab308",
              color: "#fef08a",
              background: "rgba(234, 179, 8, 0.1)",
            }}
          >
            <Sparkles size={13} /> Usar Lance Estimado RFB
          </button>
          {valorAvaliacao > 0 && (
            <button
              onClick={aplicarTetoOportunidade}
              className="btn btn-outline"
              style={{
                fontSize: "0.75rem",
                padding: "5px 10px",
                borderColor: "#10b981",
                color: "#6ee7b7",
                background: "rgba(16, 185, 129, 0.1)",
              }}
            >
              🎯 Teto Oportunidade (60% Avaliação)
            </button>
          )}
        </div>
      </div>

      {/* 3. IMPOSTOS, TAXAS E FRETE */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        gap: "12px",
        background: "rgba(255, 255, 255, 0.02)",
        padding: "14px",
        borderRadius: "10px",
        border: "1px solid rgba(255, 255, 255, 0.06)",
      }}>
        <div>
          <label style={{ fontSize: "0.75rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "4px" }}>
            <Percent size={12} /> Alíquota ICMS (%)
          </label>
          <input
            type="number"
            step={0.5}
            value={aliquotaIcms}
            onChange={(e) => setAliquotaIcms(Math.max(0, Number(e.target.value)))}
            style={{
              width: "100%",
              marginTop: "4px",
              background: "#0b0f19",
              border: "1px solid var(--border-color)",
              borderRadius: "6px",
              padding: "6px 8px",
              color: "#f8fafc",
              fontSize: "0.9rem",
            }}
          />
        </div>

        <div>
          <label style={{ fontSize: "0.75rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "4px" }}>
            <Percent size={12} /> Taxas / Comissão (%)
          </label>
          <input
            type="number"
            step={0.5}
            value={taxaComissao}
            onChange={(e) => setTaxaComissao(Math.max(0, Number(e.target.value)))}
            style={{
              width: "100%",
              marginTop: "4px",
              background: "#0b0f19",
              border: "1px solid var(--border-color)",
              borderRadius: "6px",
              padding: "6px 8px",
              color: "#f8fafc",
              fontSize: "0.9rem",
            }}
          />
        </div>

        <div>
          <label style={{ fontSize: "0.75rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "4px" }}>
            <Truck size={12} /> Frete Fixo (R$)
          </label>
          <input
            type="number"
            step={50}
            value={custoFrete}
            onChange={(e) => setCustoFrete(Math.max(0, Number(e.target.value)))}
            style={{
              width: "100%",
              marginTop: "4px",
              background: "#0b0f19",
              border: "1px solid var(--border-color)",
              borderRadius: "6px",
              padding: "6px 8px",
              color: "#f8fafc",
              fontSize: "0.9rem",
            }}
          />
        </div>

        <div>
          <label style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
            Outras Despesas (R$)
          </label>
          <input
            type="number"
            step={50}
            value={outrasDespesas}
            onChange={(e) => setOutrasDespesas(Math.max(0, Number(e.target.value)))}
            style={{
              width: "100%",
              marginTop: "4px",
              background: "#0b0f19",
              border: "1px solid var(--border-color)",
              borderRadius: "6px",
              padding: "6px 8px",
              color: "#f8fafc",
              fontSize: "0.9rem",
            }}
          />
        </div>
      </div>

      {/* 4. RESULTADO PRINCIPAL: CUSTO UNITÁRIO MÉDIO & DESEMBOLSO TOTAL */}
      <div style={{
        background: "linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.9) 100%)",
        border: "1px solid rgba(255, 255, 255, 0.12)",
        borderRadius: "14px",
        padding: "20px",
        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3)",
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "20px" }}>
          {/* Custo Unitário Médio */}
          <div>
            <span style={{ fontSize: "0.8rem", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>
              Custo Unitário Médio Real (Pós-Custos)
            </span>
            <div style={{ fontSize: "2rem", fontWeight: 800, color: "#38bdf8", marginTop: "4px", display: "flex", alignItems: "baseline", gap: "6px" }}>
              R$ {(Number(custoUnitarioMedio) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              <span style={{ fontSize: "0.9rem", color: "var(--text-muted)", fontWeight: 500 }}>/ un</span>
            </div>
            <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: "4px" }}>
              Puro do lance: R$ {(Number(custoUnitarioLancePuro) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })} / un
              {avaliacaoUnitaria && (
                <span style={{ marginLeft: "6px", color: "#a5b4fc" }}>
                  (Avaliação: R$ {(Number(avaliacaoUnitaria) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })} / un)
                </span>
              )}
            </div>
          </div>

          {/* Desembolso Total */}
          <div>
            <span style={{ fontSize: "0.8rem", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>
              Desembolso Total Necessário
            </span>
            <div style={{ fontSize: "2rem", fontWeight: 800, color: "#f8fafc", marginTop: "4px" }}>
              R$ {(Number(desembolsoTotal) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: "4px" }}>
              Lance (R$ {(Number(lance) || 0).toLocaleString("pt-BR")}) + Impostos/Taxas (R$ {(Number(resultado?.custos_adicionais?.custo_impostos_taxas) || 0).toLocaleString("pt-BR")}) + Frete (R$ {(Number(custoFrete) || 0).toLocaleString("pt-BR")})
            </div>
          </div>
        </div>

        {/* 5. BENCHMARK COM AVALIAÇÃO OFICIAL DA RECEITA FEDERAL */}
        <div style={{
          marginTop: "20px",
          paddingTop: "16px",
          borderTop: "1px solid rgba(255, 255, 255, 0.08)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "14px",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {classificacao === "OPORTUNIDADE_EXCELENTE" && <CheckCircle2 size={24} color="#10b981" />}
            {classificacao === "COMPETITIVO_MODERADO" && <TrendingUp size={24} color="#f59e0b" />}
            {classificacao === "RISCO_ELEVADO" && <ShieldAlert size={24} color="#ef4444" />}
            <div>
              <div style={{ fontSize: "0.95rem", fontWeight: 700, color: "#f8fafc" }}>
                {percentualAvaliacao !== null && percentualAvaliacao !== undefined
                  ? `${percentualAvaliacao}% da Avaliação Oficial RFB (${descontoAvaliacao}% de Deságio)`
                  : `Ágio de +${(((lance - valorMinimo) / (valorMinimo || 1)) * 100).toFixed(1)}% sobre o mínimo`}
              </div>
              <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", maxWidth: "550px" }}>
                {resultado?.benchmark_avaliacao?.motivo || "Ajuste o lance para calcular a paridade da avaliação."}
              </div>
            </div>
          </div>

          <div style={{
            padding: "6px 14px",
            borderRadius: "20px",
            fontSize: "0.8rem",
            fontWeight: 700,
            background:
              classificacao === "OPORTUNIDADE_EXCELENTE"
                ? "rgba(16, 185, 129, 0.15)"
                : classificacao === "COMPETITIVO_MODERADO"
                ? "rgba(245, 158, 11, 0.15)"
                : "rgba(239, 68, 68, 0.15)",
            color:
              classificacao === "OPORTUNIDADE_EXCELENTE"
                ? "#34d399"
                : classificacao === "COMPETITIVO_MODERADO"
                ? "#fbbf24"
                : "#f87171",
            border: `1px solid ${
              classificacao === "OPORTUNIDADE_EXCELENTE"
                ? "rgba(16, 185, 129, 0.3)"
                : classificacao === "COMPETITIVO_MODERADO"
                ? "rgba(245, 158, 11, 0.3)"
                : "rgba(239, 68, 68, 0.3)"
            }`,
          }}>
            {classificacao === "OPORTUNIDADE_EXCELENTE" && "EXCELENTE OPORTUNIDADE"}
            {classificacao === "COMPETITIVO_MODERADO" && "LANCE COMPETITIVO"}
            {classificacao === "RISCO_ELEVADO" && "RISCO ELEVADO"}
          </div>
        </div>
      </div>
    </div>
  );
};
